import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Sparkles, AlertTriangle, CheckCircle2, FileText } from "lucide-react";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const Fld = ({ label, children, hint }) => (
  <div><Label className="text-[12px]">{label}</Label>{children}{hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}</div>
);

function ExtractBanner({ review, count }) {
  const tone = review ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800";
  const Icon = review ? AlertTriangle : CheckCircle2;
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] ${tone}`} data-testid="stmt-extract-banner">
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <span className="font-semibold">Auto-extracted from your statement</span>{count ? ` · ${count} transactions read` : ""}.
        {" "}{review ? "Some figures were unclear — please verify every value against the PDF before saving." : "Values were read with high confidence — please still glance over them before saving."}
      </div>
    </div>
  );
}

function PreviousOpenWarning({ prev }) {
  if (!prev?.count) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800" data-testid="stmt-previous-open">
      <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {prev.count} previous statement{prev.count > 1 ? "s" : ""} still open · {money(prev.total_open)}{prev.overdue_count ? ` (${prev.overdue_count} overdue)` : ""}</div>
      <ul className="mt-1 space-y-0.5">
        {prev.items.slice(0, 4).map((s) => (
          <li key={s.id} className="flex justify-between"><span>{s.period_label || s.statement_date} · {s.status}</span><span className="font-medium">{money(s.outstanding)}</span></li>
        ))}
      </ul>
      <p className="mt-1 text-[11px]">If this statement's "Previous balance" matches, the older amount is carried forward — do not pay it twice.</p>
    </div>
  );
}

function TxnPreview({ tx, include, setInclude }) {
  if (!tx?.count) return null;
  return (
    <div className="rounded-lg border border-slate-200" data-testid="stmt-txn-preview">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
        <div className="text-[12px]">
          <span className="font-semibold">{tx.count} transactions read</span>
          <span className="text-slate-500"> · {tx.valid} debits {money(tx.debit_total)}</span>
          {tx.credit_count > 0 && <span className="text-emerald-700"> · {tx.credit_count} credits {money(tx.credit_total)} excluded</span>}
          {tx.duplicates > 0 && <span className="text-amber-700"> · {tx.duplicates} already recorded</span>}
        </div>
        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
          <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} data-testid="stmt-import-toggle" />
          Import &amp; tie {tx.valid} spends
        </label>
      </div>
      <div className="overflow-auto max-h-48">
        <table className="w-full text-[11px]">
          <thead className="bg-white text-slate-500 uppercase text-[9px] sticky top-0"><tr>{["Date", "Time", "Merchant", "Amount", ""].map((h) => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {tx.rows.map((r, i) => (
              <tr key={i} className={r.duplicate ? "bg-amber-50" : r.amount < 0 ? "bg-emerald-50/60" : ""} data-testid={`stmt-txn-row-${i}`}>
                <td className="px-2 py-1 whitespace-nowrap">{r.txn_date}</td><td className="px-2 py-1 text-slate-400">{r.txn_time || ""}</td>
                <td className="px-2 py-1">{r.merchant}</td>
                <td className={`px-2 py-1 text-right whitespace-nowrap ${r.amount < 0 ? "text-emerald-700" : ""}`}>{r.amount < 0 ? "+" : ""}{money(Math.abs(r.amount))}</td>
                <td className="px-2 py-1 text-[9px] text-slate-500">{r.duplicate ? "DUP" : r.amount < 0 ? "CREDIT" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY = { period_label: "", statement_date: todayStr(), due_date: todayStr(), target_pay_by: "", total_due: 0, minimum_due: 0, previous_balance: 0, payment_received: 0, purchases: 0, fees: 0, interest: 0, evidence_url: "" };

export default function StatementIntake({ cardId, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [ex, setEx] = useState(null);
  const [include, setInclude] = useState(true);
  const [verified, setVerified] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setReading(true); setEx(null);
    try {
      const { data } = await api.post(`/card-control/statements/upload?card_id=${cardId}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      const x = data.extracted || {};
      setF((s) => ({ ...s, evidence_url: data.evidence_url,
        statement_date: x.statement_date || s.statement_date, due_date: x.due_date || s.due_date,
        period_label: x.period_label || s.period_label,
        total_due: x.total_due ?? s.total_due, minimum_due: x.minimum_due ?? s.minimum_due,
        previous_balance: x.previous_balance ?? s.previous_balance, payment_received: x.payment_received ?? s.payment_received,
        purchases: data.transactions?.debit_total || s.purchases }));
      setEx(data); setVerified(false); setInclude(Boolean(data.transactions?.valid));
      toast.success(Object.keys(x).length ? "Statement read — review the figures" : "File attached (nothing could be auto-read)");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setReading(false);
  };

  const save = async () => {
    if (!f.statement_date || !f.due_date) { toast.error("Statement and due dates are required"); return; }
    if (!Number(f.total_due)) { toast.error("Total due is required"); return; }
    if (ex && !verified) { toast.error("Please confirm you have verified the extracted figures"); return; }
    setBusy(true);
    try {
      const num = (k) => Number(f[k]) || 0;
      const { data } = await api.post("/card-control/statements", {
        card_id: cardId, period_label: f.period_label || null, statement_date: f.statement_date, due_date: f.due_date,
        target_pay_by: f.target_pay_by || null, total_due: num("total_due"), minimum_due: num("minimum_due"),
        previous_balance: num("previous_balance"), payment_received: num("payment_received"),
        purchases: num("purchases"), fees: num("fees"), interest: num("interest"),
        evidence_url: f.evidence_url || null, confirmed: true,
        import_batch_id: include && ex?.transactions?.batch_id ? ex.transactions.batch_id : null });
      toast.success(data.imported_txns ? `Statement added · ${data.imported_txns} spends imported & tied` : "Statement added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const tx = ex?.transactions;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="card-stmt-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" /> Add statement</DialogTitle></DialogHeader>
        <div className="overflow-y-auto flex-1 min-h-0 space-y-3 pr-1">
          <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/40 p-3">
            <div className="flex items-center gap-2 text-[12px] text-indigo-900 font-medium"><Sparkles className="h-3.5 w-3.5" /> Upload the statement PDF — dates, totals and every transaction are read automatically</div>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.csv,.xlsx" onChange={upload} disabled={reading} className="text-[12px] mt-2" data-testid="card-stmt-file" />
            {reading && <div className="flex items-center gap-2 text-[12px] text-indigo-600 mt-2" data-testid="stmt-reading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading statement… multi-page PDFs can take up to a minute.</div>}
            {!reading && f.evidence_url && <span className="text-[10px] text-emerald-600 ml-2" data-testid="stmt-attached">Attached · {ex?.filename}</span>}
          </div>
          {ex && <ExtractBanner review={ex.review_required} count={tx?.count} />}
          {ex && <PreviousOpenWarning prev={ex.previous_open} />}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Fld label="Period label"><Input value={f.period_label} onChange={(e) => u("period_label", e.target.value)} placeholder="Jun 2026" data-testid="card-stmt-period" /></Fld>
            <Fld label="Statement date"><Input type="date" value={f.statement_date} onChange={(e) => u("statement_date", e.target.value)} data-testid="card-stmt-date" /></Fld>
            <Fld label="Due date"><Input type="date" value={f.due_date} onChange={(e) => u("due_date", e.target.value)} data-testid="card-stmt-due" /></Fld>
            <Fld label="Total due"><Input type="number" value={f.total_due} onChange={(e) => u("total_due", e.target.value)} data-testid="card-stmt-total" /></Fld>
            <Fld label="Minimum due"><Input type="number" value={f.minimum_due} onChange={(e) => u("minimum_due", e.target.value)} data-testid="card-stmt-min" /></Fld>
            <Fld label="Target pay-by" hint="Blank = 7 days before due"><Input type="date" value={f.target_pay_by} onChange={(e) => u("target_pay_by", e.target.value)} data-testid="card-stmt-target" /></Fld>
            <Fld label="Previous balance"><Input type="number" value={f.previous_balance} onChange={(e) => u("previous_balance", e.target.value)} data-testid="card-stmt-prev" /></Fld>
            <Fld label="Payment received"><Input type="number" value={f.payment_received} onChange={(e) => u("payment_received", e.target.value)} data-testid="card-stmt-received" /></Fld>
            <Fld label="Purchases"><Input type="number" value={f.purchases} onChange={(e) => u("purchases", e.target.value)} data-testid="card-stmt-purchases" /></Fld>
            <Fld label="Fees"><Input type="number" value={f.fees} onChange={(e) => u("fees", e.target.value)} /></Fld>
            <Fld label="Interest"><Input type="number" value={f.interest} onChange={(e) => u("interest", e.target.value)} /></Fld>
          </div>
          <TxnPreview tx={tx} include={include} setInclude={setInclude} />
          {ex && (
            <label className="flex items-center gap-2 text-[12px] cursor-pointer rounded-lg border border-slate-200 px-3 py-2">
              <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} data-testid="stmt-verified-check" />
              I have verified the extracted dates and amounts against the statement.
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={save} disabled={busy || reading} data-testid="card-stmt-dialog-save">
            {busy ? "Saving…" : include && tx?.valid ? `Save statement + import ${tx.valid} spends` : "Save statement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
